import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { PropertyLoanSection } from "./components/PropertyLoanSection";
import { IncomeSection } from "./components/IncomeSection";
import { ExpenditureSection } from "./components/ExpenditureSection";
import { ContactDetailsSection } from "./components/ContactDetailsSection";
import { ActionButtons } from "./components/ActionButtons";
import { MortgageResultsSection } from "../results/components/MortgageResultsSection";
import { MortgageFormLayout } from "./components/MortgageFormLayout";
import { MortgageFormData } from "../../types/mortgage";
import { useFormValidation } from "./hooks/useFormValidation";
import { useCalculationInputs } from "./hooks/useCalculationInputs";
import { useResultsSaving } from "./hooks/useResultsSaving";
import { toast } from "sonner";
import { logger } from "../../lib/logger";

const defaultFormData: MortgageFormData = {
  household: {
    adults: 1,
    children: 0,
  },
  property: {
    propertyValue: 0,
    loanRequired: 0,
    loanTermMonths: 0,
    payRate: 0,
    fiveYearFixedTerm: true,
    repaymentType: "Repayment",
    amountInterestOnly: 0,
    loanType: "Residential",
  },
  income: {
    totalIncomeNetOfTax: 0,
    foreignIncome: 0,
    foreignCurrency: "US$",
    incomeFromProperties: 0,
  },
  expenditure: {
    householdOnsOverride: 0,
    commitments: {
      creditCommitments: 0,
      additionalCommitments: 0,
      pensionContributions: 0,
      otherMortgages: 0,
    },
  },
  background: {
    btlTotalValueOfBorrowing: 0,
    otherTotalValueOfBorrowing: 0,
  },
  contact: {
    name: "",
    email: "",
    telephone: "",
  },
};

interface MortgageFormProps {
  loadedFormData?: MortgageFormData;
  onFormDataLoaded?: () => void;
}

export function MortgageForm({ loadedFormData, onFormDataLoaded }: MortgageFormProps) {
  const [formData, setFormData] = useState<MortgageFormData>(defaultFormData);
  const [showResults, setShowResults] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [isLoadingExistingData, setIsLoadingExistingData] = useState(false);
  const [skipAutoSave, setSkipAutoSave] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationResults, setCalculationResults] = useState<{
    mortgageCalculations: any;
    totalCostsCalculations: any;
    maxLoanData: any;
  } | null>(null);
  const hasLoadedDataRef = useRef(false);

  const householdExpenditure = useQuery(api.onsExpenditure.calculateHouseholdExpenditure, {
    adults: formData.household.adults,
    children: formData.household.children,
  });
  const onsMatrix = useQuery(api.onsExpenditure.getCurrentOnsDefaults);

  // Custom hooks
  const validation = useFormValidation(formData, null);
  const { saveResults } = useResultsSaving();

  // Calculate effective household expenditure
  const calculatedOnsValue = typeof householdExpenditure === 'number' ? householdExpenditure : householdExpenditure?.totalMonthly || 0;
  const effectiveHouseholdExpenditure = (formData.expenditure.householdOnsOverride > 0 && formData.expenditure.householdOnsOverride > calculatedOnsValue)
    ? formData.expenditure.householdOnsOverride 
    : calculatedOnsValue;

  const { mainCalculationInputs, totalCostsInputs } = useCalculationInputs(formData, effectiveHouseholdExpenditure);

  // Mutations for manual calculation
  const calculateMortgageVariables = useMutation(api.calculationMutations.calculateMortgageVariables);
  const calculateTotalCosts = useMutation(api.calculationMutations.calculateTotalCosts);
  const calculateMaxLoan = useMutation(api.calculationMutations.calculateMaxLoan);

  // Log form access
  useEffect(() => {
    logger.info("Mortgage form accessed");
  }, []);

  // Handle loaded form data from saved results
  useEffect(() => {
    if (loadedFormData) {
      logger.info("Loading saved calculation result into form");
      hasLoadedDataRef.current = true;
      setIsLoadingExistingData(true);
      setSkipAutoSave(true);
      setFormData(loadedFormData);
      
      // For loaded data, just show the form - user needs to click calculate to see results
      setShowResults(false);
      setShowValidation(false);
      
      if (onFormDataLoaded) {
        onFormDataLoaded();
      }
      
      // Brief loading state then ready for user to calculate
      setTimeout(() => {
        setIsLoadingExistingData(false);
      }, 500);
    }
  }, [loadedFormData, onFormDataLoaded]);

  // Reset skipAutoSave when loadedFormData is cleared
  useEffect(() => {
    if (!loadedFormData && skipAutoSave) {
      setSkipAutoSave(false);
    }
  }, [loadedFormData, skipAutoSave]);

  // Keep skipAutoSave true while we have loaded data
  useEffect(() => {
    if (loadedFormData) {
      setSkipAutoSave(true);
    }
  }, [loadedFormData]);


  // Log when calculation results are available
  useEffect(() => {
    if (calculationResults?.maxLoanData && showResults) {
      logger.calculationCompleted(calculationResults.maxLoanData);
    }
  }, [calculationResults?.maxLoanData, showResults]);

  const handleCalculate = async (skipSave = false) => {
    logger.info("Calculate button clicked", { skipSave });
    
    const propertyValid = validation.isPropertyLoanValid();
    const incomeValid = validation.isIncomeValid();
    const contactValid = validation.isContactDetailsValid();
    const isFullyValid = propertyValid && incomeValid && contactValid;
    
    if (!isFullyValid) {
      logger.validationError("form_validation", "Form validation failed");
      setShowValidation(true);
      validation.focusFirstInvalidField();
      toast.error("Please complete all required fields correctly before calculating.");
      return;
    }
    
    setIsCalculating(true);
    
    try {
      logger.calculationStarted(formData);
      
      // Perform calculations
      const [mortgageCalcs, totalCostsCalcs, maxLoanCalcs] = await Promise.all([
        calculateMortgageVariables(mainCalculationInputs),
        calculateTotalCosts(totalCostsInputs),
        calculateMaxLoan(mainCalculationInputs)
      ]);
      
      // Store results in state
      setCalculationResults({
        mortgageCalculations: mortgageCalcs,
        totalCostsCalculations: totalCostsCalcs,
        maxLoanData: maxLoanCalcs
      });
      
      setShowResults(true);
      
      // Save results unless explicitly skipped
      if (!skipSave && !isLoadingExistingData && !hasLoadedDataRef.current) {
        await saveResults(formData, maxLoanCalcs, householdExpenditure);
      }
      
      const message = skipSave 
        ? "Calculation completed (not saved)" 
        : "Calculation completed and saved";
      toast.success(message);
      
      // Scroll to results section
      setTimeout(() => {
        const resultsElement = document.getElementById('mortgage-results');
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      
    } catch (error) {
      logger.error("Calculation failed", error as Error);
      toast.error("Calculation failed. Please try again.");
    } finally {
      setIsCalculating(false);
    }
  };

  const handleReset = () => {
    logger.formReset();
    setFormData(defaultFormData);
    setShowResults(false);
    setShowValidation(false);
    setSkipAutoSave(false);
    setCalculationResults(null);
    hasLoadedDataRef.current = false;
    toast.success("Form has been reset");
  };

  const handleFormDataChange = (section: keyof MortgageFormData, data: any) => {
    setFormData(prev => ({ ...prev, [section]: data }));
    logger.debug(`${section} data updated`, { [section]: data });
    
    // Clear results when form data changes (except when loading existing data)
    if (!isLoadingExistingData && showResults) {
      setShowResults(false);
      setCalculationResults(null);
    }
    
    // Reset the loaded data flag when user makes changes
    if (!isLoadingExistingData && hasLoadedDataRef.current) {
      hasLoadedDataRef.current = false;
      logger.debug("Reset loaded data flag - user made changes to form");
    }
  };

  const leftColumn = (
    <>
      <PropertyLoanSection
        data={formData.property}
        onChange={(property) => handleFormDataChange('property', property)}
        mortgageCalculations={calculationResults?.mortgageCalculations}
        showValidation={showValidation}
        isAdmin={false}
      />

      <IncomeSection
        data={formData.income}
        onChange={(income) => handleFormDataChange('income', income)}
        mortgageCalculations={calculationResults?.mortgageCalculations}
        showValidation={showValidation}
        isAdmin={false}
      />
    </>
  );

  const rightColumn = (
    <ExpenditureSection
      household={formData.household}
      expenditure={formData.expenditure}
      background={formData.background}
      onHouseholdChange={(household) => handleFormDataChange('household', household)}
      onExpenditureChange={(expenditure) => handleFormDataChange('expenditure', expenditure)}
      onBackgroundChange={(background) => handleFormDataChange('background', background)}
      householdExpenditure={householdExpenditure}
      mortgageCalculations={calculationResults?.mortgageCalculations}
      totalCostsCalculations={calculationResults?.totalCostsCalculations}
      onsMatrix={onsMatrix}
      isAdmin={false}
    />
  );

  // Show contact section for all users (WorkOS users can update their contact info)
  const contactSection = (
    <ContactDetailsSection
      data={formData.contact}
      onChange={(contact) => handleFormDataChange('contact', contact)}
      showValidation={showValidation}
      readonlyFields={{
        name: false,
        email: false,
        telephone: false,
      }}
    />
  );

  const actionButtons = (
    <ActionButtons
      onCalculate={handleCalculate}
      onReset={handleReset}
      showResults={showResults}
      isAdmin={false}
      isCalculating={isCalculating}
    />
  );

  const resultsSection = showResults && calculationResults ? (
    <MortgageResultsSection
      maxLoanData={calculationResults.maxLoanData}
      formData={formData}
      mortgageCalculations={calculationResults.mortgageCalculations}
      totalCostsCalculations={calculationResults.totalCostsCalculations}
      householdExpenditure={householdExpenditure}
      isAdmin={false}
      isLoadingExistingData={isLoadingExistingData}
    />
  ) : null;

  return (
    <MortgageFormLayout
      title="Maximum Loan Calculator"
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      contactSection={contactSection}
      actionButtons={actionButtons}
      resultsSection={resultsSection}
      showResults={showResults}
    />
  );
}
